import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import client from '../API/API';
import { SkeletonLine } from '../components/ui/Skeleton';

// ── Design-token colours for charts ──────────────────────────────────────────
const ACCENT  = '#0897BE';
const ONLINE  = '#00875A';
const OFFLINE = '#C42B1C';
const WARN    = '#B45309';
const AI_COL  = '#6938EF';

const PALETTE = [ACCENT, ONLINE, OFFLINE, WARN, AI_COL, '#F59E0B', '#06D6A0', '#FB8500'];

// ── Chart axis / grid shared base ────────────────────────────────────────────
const axisBase = {
  axisLabel: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: '#B6BACA' },
  axisLine:  { lineStyle: { color: '#ECEDF1' } },
  axisTick:  { show: false },
  splitLine: { lineStyle: { type: 'dashed', color: '#ECEDF1' } },
};

// ── Shared animation config for all ECharts instances ────────────────────────
const chartAnimation = {
  animation: true,
  animationDuration: 900,
  animationEasing: 'cubicOut',
  animationDurationUpdate: 500,
  animationEasingUpdate: 'cubicInOut',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const normalize     = (v = '') => String(v).toLowerCase().replace(/[^a-z0-9]/g, '');
const includesAny   = (v, arr) => arr.some(s => v.includes(s));
const parseMaybeNum = (v) => { const n = parseFloat(v); return Number.isNaN(n) ? null : n; };
const hmsToMinutes  = (v) => {
  if (!v || typeof v !== 'string' || !v.includes(':')) return null;
  const [h, m, s] = v.split(':').map(Number);
  if ([h, m, s].some(Number.isNaN)) return null;
  return Math.round(((h * 3600 + m * 60 + s) / 60) * 100) / 100;
};

const collectHours = (seriesList = []) => {
  const seen = new Set(), ordered = [];
  seriesList.forEach(s => (s.data || []).forEach(p => { if (!seen.has(p.hour)) { seen.add(p.hour); ordered.push(p.hour); } }));
  return ordered.sort((a, b) => a.localeCompare(b));
};

const buildValues = (series, hours, parser) => {
  const map = new Map((series.data || []).map(p => [p.hour, p.value]));
  return hours.map(h => parser(map.get(h)));
};

const getLastValue = (series, parser) => {
  const vals = (series.data || []).map(p => parser(p.value)).filter(v => v != null);
  return vals.length > 0 ? vals[vals.length - 1] : 0;
};

// ── Chart card wrapper ────────────────────────────────────────────────────────
function ChartCard({ title, subtitle, children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-hair)',
        borderRadius: 'var(--r-lg)',
        padding: 'var(--card-pad)',
        boxShadow: 'var(--sh-sm)',
      }}
    >
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: 13, color: 'var(--ink-900)' }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--ink-500)', marginTop: 3 }}>
            {subtitle}
          </div>
        )}
      </div>
      {children}
    </motion.div>
  );
}

// ── Multi-series line chart (Environmental) ───────────────────────────────────
function MultiSeriesChart({ title, subtitle, hours, seriesList, valueParser, delay }) {
  const seriesColors = [ACCENT, ONLINE, OFFLINE, WARN, AI_COL];
  const option = {
    ...chartAnimation,
    color: seriesList.map((_, i) => seriesColors[i % seriesColors.length]),
    tooltip: {
      trigger: 'axis', backgroundColor: '#fff',
      borderColor: '#ECEDF1', textStyle: { fontFamily: 'DM Sans', fontSize: 12 },
      formatter: (params) => {
        const lines = [`<b>${params[0]?.axisValue || ''}</b>`];
        params.forEach(p => lines.push(`${p.marker} ${p.seriesName}: ${p.data ?? '—'}`));
        return lines.join('<br/>');
      },
    },
    legend: { top: 0, type: 'scroll', textStyle: { fontFamily: 'DM Sans', fontSize: 11 } },
    grid: { top: 54, left: 52, right: 16, bottom: 46 },
    xAxis: { type: 'category', data: hours, ...axisBase, axisLabel: { ...axisBase.axisLabel, rotate: hours.length > 12 ? 35 : 0 } },
    yAxis: { type: 'value', min: 0, ...axisBase, splitLine: axisBase.splitLine },
    series: seriesList.map((s, i) => ({
      name: s.display_name, type: 'line',
      data: buildValues(s, hours, valueParser),
      smooth: true, showSymbol: false,
      lineStyle: { width: 2, color: seriesColors[i % seriesColors.length] },
      areaStyle: { color: seriesColors[i % seriesColors.length], opacity: 0.08 },
    })),
  };
  return (
    <ChartCard title={title} subtitle={subtitle} delay={delay}>
      <ReactECharts option={option} style={{ height: 280 }} />
    </ChartCard>
  );
}

// ── Runtime vs Idle (horizontal stacked bar) ──────────────────────────────────
function StackedUtilizationChart({ title, subtitle, seriesList, delay }) {
  const bars = seriesList.map((s, i) => ({
    name: s.display_name, value: getLastValue(s, hmsToMinutes),
    color: i === 0 ? '#0C0E16' : ACCENT,
  }));
  const option = {
    ...chartAnimation,
    tooltip: {
      trigger: 'axis', axisPointer: { type: 'shadow' }, backgroundColor: '#fff',
      borderColor: '#ECEDF1', textStyle: { fontFamily: 'DM Sans', fontSize: 12 },
      formatter: (params) => {
        const total = params.reduce((s, p) => s + (p.value || 0), 0);
        return [`<b>Selected Day</b>`, `Total: ${total} min`, ...params.map(p => `${p.marker} ${p.seriesName}: ${p.value || 0} min`)].join('<br/>');
      },
    },
    legend: { top: 0, textStyle: { fontFamily: 'DM Sans', fontSize: 11 } },
    grid: { top: 48, left: 24, right: 24, bottom: 24, containLabel: true },
    xAxis: { type: 'value', min: 0, ...axisBase, name: 'Minutes', nameLocation: 'middle', nameGap: 28 },
    yAxis: { type: 'category', data: ['Selected Day'], axisLabel: { fontFamily: 'DM Sans', fontSize: 12 } },
    series: bars.map(b => ({
      name: b.name, type: 'bar', stack: 'util', data: [b.value], barWidth: 40,
      itemStyle: { color: b.color, borderRadius: b.name.toLowerCase().includes('idle') ? [0, 2, 2, 0] : [2, 0, 0, 2] },
      label: { show: b.value > 0, position: 'inside', formatter: ({ value }) => `${value} min`, color: '#fff', fontSize: 11, fontWeight: 600 },
    })),
  };
  return (
    <ChartCard title={title} subtitle={subtitle} delay={delay}>
      <ReactECharts option={option} style={{ height: 160 }} />
    </ChartCard>
  );
}

// ── Single-series chart ───────────────────────────────────────────────────────
function SingleSeriesChart({ chart, index, delay }) {
  const { display_name, chart_type, data } = chart;
  const color  = PALETTE[index % PALETTE.length];
  const hours  = data.map(d => d.hour);
  const values = data.map(d => { const n = parseFloat(d.value); return Number.isNaN(n) ? null : n; });

  const option = {
    ...chartAnimation,
    color: [color],
    tooltip: {
      trigger: 'axis', backgroundColor: '#fff', borderColor: '#ECEDF1',
      textStyle: { fontFamily: 'DM Sans', fontSize: 12 },
      formatter: (params) => {
        const p = params[0];
        return `${p.axisValue}<br/>${display_name}: ${data[p.dataIndex]?.value ?? p.value}`;
      },
    },
    grid: { top: 28, left: 52, right: 16, bottom: 46 },
    xAxis: { type: 'category', data: hours, ...axisBase, axisLabel: { ...axisBase.axisLabel, rotate: hours.length > 12 ? 35 : 0 } },
    yAxis: { type: 'value', min: 0, ...axisBase },
    series: [{
      name: display_name, type: chart_type, data: values,
      smooth: chart_type === 'line', showSymbol: chart_type === 'line' && data.length <= 24,
      barMaxWidth: 32,
      itemStyle: { color, borderRadius: chart_type === 'bar' ? [2, 2, 0, 0] : undefined },
      lineStyle: chart_type === 'line' ? { width: 2, color } : undefined,
      areaStyle: chart_type === 'line' ? { color, opacity: 0.08 } : undefined,
    }],
  };

  return (
    <ChartCard title={display_name} delay={delay}>
      <ReactECharts option={option} style={{ height: 240 }} />
    </ChartCard>
  );
}

// ── Skeleton chart placeholder ────────────────────────────────────────────────
function SkeletonChart({ full = false }) {
  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border-hair)',
      borderRadius: 'var(--r-lg)', padding: 'var(--card-pad)', boxShadow: 'var(--sh-sm)',
    }}>
      <SkeletonLine width="40%" height={13} style={{ marginBottom: 8 }} />
      <SkeletonLine width="60%" height={10} style={{ marginBottom: 20 }} />
      <SkeletonLine width="100%" height={full ? 240 : 180} />
    </div>
  );
}

// ── Main KPI container ────────────────────────────────────────────────────────
const KpiMachines = ({ machineid }) => {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [charts, setCharts] = useState([]);
  const [date,   setDate]   = useState(todayStr);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!machineid) return;
    setLoading(true); setError(''); setCharts([]);
    client.get('/api/kpi/', { params: { machine_id: machineid, date } })
      .then(res => setCharts(res.data.charts || []))
      .catch(err => { console.error(err); setError('Could not load KPI data.'); })
      .finally(() => setLoading(false));
  }, [machineid, date]);

  const groupedCharts = useMemo(() => {
    const environmental = [], timeCharts = [], singles = [];
    charts.forEach(chart => {
      const key = normalize(chart.internal_name || chart.display_name);
      if (includesAny(key, ['waterlevel', 'tanklevel'])) return;
      if (includesAny(key, ['temperature', 'humidity', 'dewpoint'])) environmental.push(chart);
      else if (includesAny(key, ['runtime', 'idletime'])) timeCharts.push(chart);
      else singles.push(chart);
    });

    const result = [];
    if (environmental.length > 0) result.push({ key: 'env', layout: 'full', node: <MultiSeriesChart key="env" title="Environmental Conditions" subtitle="Temperature, Humidity & Dew Point" hours={collectHours(environmental)} seriesList={environmental} valueParser={parseMaybeNum} delay={0.1} /> });
    if (timeCharts.length > 0)     result.push({ key: 'rt',  layout: 'full', node: <StackedUtilizationChart key="rt" title="Runtime vs Idle Time" subtitle="Daily utilization for selected date" seriesList={timeCharts} delay={0.15} /> });
    singles.forEach((chart, i)     => result.push({ key: chart.internal_name, layout: 'grid', node: <SingleSeriesChart key={chart.internal_name} chart={chart} index={i} delay={0.1 + i * 0.05} /> }));
    return result;
  }, [charts]);

  const fullCharts = groupedCharts.filter(i => i.layout === 'full');
  const gridCharts = groupedCharts.filter(i => i.layout === 'grid');

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: 13, color: 'var(--ink-900)' }}>
            Hourly Data · <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 400 }}>{date}</span>
          </div>
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--ink-500)', marginTop: 3 }}>
            Last record per hour from machine log
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--ink-500)', marginBottom: 4 }}>
            Date
          </label>
          <input
            type="date"
            value={date}
            max={todayStr}
            onChange={e => setDate(e.target.value || todayStr)}
            style={{
              padding: '7px 11px',
              fontFamily: 'var(--font-mono)', fontSize: 12,
              color: 'var(--ink-900)',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--r-md)', outline: 'none', cursor: 'pointer',
            }}
          />
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--grid-gap)' }}>
          <SkeletonChart full />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--grid-gap)' }}>
            <SkeletonChart /><SkeletonChart />
          </div>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div style={{ padding: '40px 24px', textAlign: 'center', fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--offline)' }}>
          {error}
        </div>
      )}

      {/* No data */}
      {!loading && !error && charts.length === 0 && (
        <div style={{ padding: '60px 24px', textAlign: 'center', fontFamily: 'var(--font-sans)', fontSize: 14, color: 'var(--ink-500)' }}>
          No KPI data available for this date.
        </div>
      )}

      {/* Charts */}
      {!loading && !error && charts.length > 0 && (
        <>
          {fullCharts.map(item => (
            <div key={item.key} style={{ marginBottom: 'var(--grid-gap)' }}>{item.node}</div>
          ))}
          {gridCharts.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(340px, 1fr))`,
              gap: 'var(--grid-gap)',
            }}>
              {gridCharts.map(item => <div key={item.key}>{item.node}</div>)}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default KpiMachines;
